import {
  formatDate,
  getHours,
  getMinutes,
  isPast,
  isSameDay,
  isToday,
  parseISO,
  set,
} from 'date-fns';
import { isFirstVisible } from './header/helpers';
import { WorkloadData, WorkloadDraggableItem } from '@contexts/workload/types';
import { WorkloadCalendarSchedule } from '@services/workload/calendar/types';
import { getFirstDayOfBusinessWeek, getLastDayOfBusinessWeek } from '@contexts/workload/constants';
import { formatTime } from '@utils/i18n';
import { Errors } from '@hooks/errors/types';
import {
  CALENDAR_BORDERS,
  FOREMAN_COLUMN_WIDTH,
  MIN_DAY_COLUMN_WIDTH,
  MIN_VISIBLE_DAYS,
} from './constants';

export const getDroppedDateDetails = (
  startsAt: string,
  endsAt: string,
  draggableItem: WorkloadDraggableItem,
) => [
  set(draggableItem.date, {
    hours: startsAt ? getHours(parseISO(startsAt)) : undefined,
    minutes: startsAt ? getMinutes(parseISO(startsAt)) : undefined,
  }),
  set(draggableItem.date, {
    hours: endsAt ? getHours(parseISO(endsAt)) : undefined,
    minutes: endsAt ? getMinutes(parseISO(endsAt)) : undefined,
  }),
];

export const toErrorMessages = (errors?: Errors): string[] => {
  if (!errors) return [];
  if (typeof errors === 'string') return [errors];
  if (Array.isArray(errors)) return errors;
  return Object.values(errors).flatMap((value) => (Array.isArray(value) ? value : [value]));
};

export const overlappingDetailsSummary = (newStartsAt: Date, newEndsAt: Date) =>
  `${[formatTime(new Date(newStartsAt || '')), formatTime(new Date(newEndsAt || ''))].join(' - ')}, ${formatDate(new Date(newStartsAt || ''), 'MMM do, yyyy')}`;

export const isFirstDayOfBusinessWeek = (date: Date): boolean =>
  date.getTime() === getFirstDayOfBusinessWeek(date).getTime();

export const isLastDayOfBusinessWeek = (date: Date): boolean =>
  date.getTime() === getLastDayOfBusinessWeek(date).getTime();

export const isBeforeToday = (date: Date): boolean => isPast(date) && !isToday(date);

export const isLastVisible = (dates: Date[], index: number): boolean => index === dates.length - 1;

export const appendSchedulesToDates = (
  dates: Date[],
  schedules: WorkloadCalendarSchedule[],
): WorkloadCalendarSchedule[][] =>
  dates.map((date) =>
    schedules.filter((schedule) => isSameDay(date, parseISO(schedule.startDate))),
  );

export const getBoundaryItemStyles = (date: Date, index: number, upperHeader?: boolean) => [
  isFirstDayOfBusinessWeek(date) &&
    !isFirstVisible(index) &&
    'border-l-[5px] border-l-neutral-100 after:absolute after:left-[-5px] after:-top-1 after:h-1 after:w-[5px] after:bg-neutral-100',
  !upperHeader && isBeforeToday(date) && 'bg-neutral-50',
];

const TOLERANCE = 20;

const getThresholds = (initialDays: number, minDays: number) =>
  Array.from(
    { length: Math.max(initialDays - minDays, 0) },
    (_, index) =>
      FOREMAN_COLUMN_WIDTH +
      CALENDAR_BORDERS +
      (initialDays - index) * MIN_DAY_COLUMN_WIDTH +
      TOLERANCE,
  );

export const changeVisibleDays = (
  setData: (prop: keyof WorkloadData, value: unknown) => void,
  calendarWidth: number,
  visibleDays: number,
  initialDays: number,
  minDays = MIN_VISIBLE_DAYS,
) => {
  const THRESHOLDS = getThresholds(initialDays, minDays);
  let targetFewerDays = initialDays - visibleDays;

  while (targetFewerDays > 0 && calendarWidth >= THRESHOLDS[targetFewerDays - 1] + TOLERANCE) {
    targetFewerDays -= 1;
  }
  while (
    targetFewerDays < THRESHOLDS.length &&
    calendarWidth < THRESHOLDS[targetFewerDays] - TOLERANCE
  ) {
    targetFewerDays += 1;
  }

  if (targetFewerDays !== initialDays - visibleDays) {
    setData('visibleDays', initialDays - targetFewerDays);
  }
};

export const getDraggableStyles = (date: Date, draggableItem?: WorkloadDraggableItem) => {
  if (!draggableItem) return;
  if (!draggableItem?.isDragging) return;
  return isBeforeToday(date) ? 'bg-neutral-100' : 'bg-neutral-50';
};

export const getDayColumnWidth = (calendarWidth: number, visibleDays: number) =>
  Math.max(
    MIN_DAY_COLUMN_WIDTH,
    Math.floor((calendarWidth - FOREMAN_COLUMN_WIDTH - CALENDAR_BORDERS) / visibleDays),
  );
